import { jwtDecode } from "jwt-decode";
import { Message } from "./utils/types";

export const generateUUID = () => {
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0,
      v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

export const isIOS = () => {
  const ua = navigator.userAgent.toLowerCase();
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  return (
    ua.includes("iphone") ||
    ua.includes("ipad") ||
    ua.includes("ipod") ||
    isSafari
  );
};

export function getDecodedSCToken(token: string) {
  return token ? jwtDecode(token) : null;
}

// Function to process conversation summary
export const processConversationSummary = (summary: string) => {
  // First, clean up the escaped string
  const cleanedSummary = summary
    .replace(/\\n/g, "\n") // Replace escaped newlines
    .replace(/\\\"/g, '"') // Replace escaped quotes
    .replace(/\"\\n/g, "\n") // Replace escaped quotes with newlines
    .replace(/\\n\"/g, "\n") // Replace escaped quotes with newlines
    .replace(/^"|"$/g, ""); // Remove starting and ending quotes

  // Split into individual conversation chunks
  const conversations = cleanedSummary.split('\n\n"');

  // Process for messages
  let newMessages: Message[] = [];

  conversations.forEach((conversation) => {
    const lines = conversation.split("\n");
    let currentRole = "";
    let currentMessage = "";

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();

      if (line.startsWith("User:")) {
        if (currentRole === "Agent" && currentMessage.trim()) {
          newMessages.push({
            type: "agent",
            text: currentMessage.trim(),
            timestamp: Date.now(),
          });
        }
        currentRole = "User";
        currentMessage = line.replace("User:", "").trim();

        // Look ahead for multi-line messages
        while (
          i + 1 < lines.length &&
          !lines[i + 1].startsWith("Agent:") &&
          !lines[i + 1].startsWith("User:")
        ) {
          i++;
          if (lines[i].trim()) {
            currentMessage += " " + lines[i].trim();
          }
        }

        if (currentMessage) {
          newMessages.push({
            type: "user",
            text: currentMessage.trim(),
            timestamp: Date.now(),
          });
        }
        currentMessage = "";
      } else if (line.startsWith("Agent:")) {
        if (currentRole === "User" && currentMessage.trim()) {
          newMessages.push({
            type: "user",
            text: currentMessage.trim(),
            timestamp: Date.now(),
          });
        }
        currentRole = "Agent";
        currentMessage = line.replace("Agent:", "").trim();

        // Look ahead for multi-line messages
        while (
          i + 1 < lines.length &&
          !lines[i + 1].startsWith("Agent:") &&
          !lines[i + 1].startsWith("User:")
        ) {
          i++;
          if (lines[i].trim()) {
            currentMessage += " " + lines[i].trim();
          }
        }

        if (currentMessage) {
          newMessages.push({
            type: "agent",
            text: currentMessage.trim(),
            timestamp: Date.now(),
          });
        }
        currentMessage = "";
      }
    }

    // Handle the last message if any
    if (currentMessage.trim()) {
      newMessages.push({
        type: currentRole.toLowerCase() as "user" | "agent",
        text: currentMessage.trim(),
        timestamp: Date.now(),
      });
    }
  });

  return newMessages;
};

export const isConnectionActive = (peerConnectionRef: any): boolean => {
  return !!(
    peerConnectionRef &&
    (peerConnectionRef.connectionState === "connected" ||
      peerConnectionRef.connectionState === "connecting" ||
      peerConnectionRef.iceConnectionState === "connected" ||
      peerConnectionRef.iceConnectionState === "checking")
  );
};

export const checkAndRequestPermissions = async () => {
  try {
    // First check if permissions API is available
    if (navigator.permissions && navigator.permissions.query) {
      const result = await navigator.permissions.query({
        name: "microphone" as PermissionName,
      });
      if (result.state === "denied") {
        throw new Error("Microphone permission is denied");
      }
    }

    // Try to get audio stream with basic constraints first
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    stream.getTracks().forEach((track) => track.stop());
    return true;
  } catch (error) {
    console.error("Permission check failed:", error);
    return false;
  }
};

export const initializeAudioContext = async () => {
  try {
    const AudioContext =
      window.AudioContext || (window as any).webkitAudioContext;
    const audioContext = new AudioContext({
      // Safari-specific settings
      latencyHint: "interactive",
      sampleRate: 44100,
    });

    // Wait for audio context to be ready
    if (audioContext.state === "suspended") {
      await audioContext.resume();
    }

    return audioContext;
  } catch (error) {
    console.error("Audio Context initialization error:", error);
    throw new Error("Failed to initialize audio system");
  }
};

export function interpolate(
  str: string,
  vars: Record<string, string | number>
): string {
  return str.replace(/{(.*?)}/g, (_, key) =>
    vars[key] !== undefined ? String(vars[key]) : ""
  );
}
